'use client';

import { EcEnv } from '@castlery/config';
import { Box, Button, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { Communication } from '@castlery/fortress/Icons';
import React, { useState, useEffect, useCallback } from 'react';
import { GladlyLoadScript } from '../gladly-box/gladly-inject';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectedActiveUser } from '@castlery/modules-user-domain';
import { CasaLoadScript } from './casa-box';
import { AddToCartParams } from '@castlery/utils';
import * as Sentry from '@sentry/nextjs';
import { logger } from '@castlery/observability';

enum CustomerServiceSystem {
  CASA,
  GLADLY,
  WHATSAPP,
}

function chooseCustomerServiceBoxSystem(
  market: string,
  isCasaEnabled: boolean,
  hasCasaException: boolean,
  supportMarkets: string[]
) {
  if (isCasaEnabled && !hasCasaException && supportMarkets.some((m) => m.toUpperCase() === market.toUpperCase())) {
    return CustomerServiceSystem.CASA;
  }

  if (market.toUpperCase() === 'SG') {
    return CustomerServiceSystem.WHATSAPP;
  }

  return CustomerServiceSystem.GLADLY;
}

/**
 * Manage Customer Service Box logic here
 *
 * Note:
 * We have many markets, such as US, SG, AU, CA, UK etc.
 *
 * 1. the first version of the Casa System only support SG market
 * 2. other markets will use Gladly system in the future
 * 3. if the Casa System loaded error, we will fallback to Gladly system for SG market
 */

const CustomerServiceBoxImpl = () => {
  const { mobile } = useBreakpoints();
  // Is the Non Casa Casa Box activated
  const [isNonCasaChatBoxActive, setIsNonCasaChatBoxActive] = useState(false);
  // Is the Non Casa Casa Box Chat Icon need to hide
  const [isNonCasaChatIconHide, setIsNoneCasaChatIconHide] = useState(false);
  const currentUser = useAppSelector(selectedActiveUser);

  const [casaException, setCasaException] = useState(false);
  const [customerServiceBoxSystem, setCustomerServiceBoxSystem] = useState<CustomerServiceSystem | null>(null);
  const loadCasaTimeoutRef = React.useRef<number | null>(null);

  useEffect(() => {
    const { enabled: isCasaEnabled, supportMarket: casaSupportMarkets } = window.__CASA_CONFIG__ || {};

    const { forceEnabled } = window.__CASA_ENV_CONFIG__ || {};
    const isCasaForceEnabled = forceEnabled ? forceEnabled.includes(EcEnv.NEXT_PUBLIC_APPLICATION_ENV) : false;

    const isCasaEnabledFinal = isCasaForceEnabled || Boolean(isCasaEnabled);

    const newCustomerServiceBoxSystem = chooseCustomerServiceBoxSystem(
      EcEnv.NEXT_PUBLIC_COUNTRY,
      isCasaEnabledFinal,
      casaException,
      casaSupportMarkets || []
    );

    if (newCustomerServiceBoxSystem !== customerServiceBoxSystem) {
      setCustomerServiceBoxSystem(newCustomerServiceBoxSystem);
    }

    if (newCustomerServiceBoxSystem === CustomerServiceSystem.CASA) {
      if (!window.__CASA_CONFIG__) {
        window.__CASA_CONFIG__ = {};
      }

      if (!window.__CASA_CONFIG__.features) {
        window.__CASA_CONFIG__.features = {};
      }

      if (!window.__CASA_CONFIG__.features.addToCart) {
        window.__CASA_CONFIG__.features.addToCart = ({ product, variant, quantity }: AddToCartParams) => {
          if (!window.clsr || !window.clsr.aiChatUtils) {
            return Promise.reject(new Error('[AiChatUtils Error]: AiChatUtils is not initialized'));
          }

          const isValid = window.clsr.aiChatUtils.init('bcee8217-f3e0-44b4-823f-43519bef40d3');

          if (!isValid) {
            return Promise.reject(new Error('[AiChatUtils Error]: Invalid token for AiChatUtils'));
          }

          return window.clsr.aiChatUtils.addToCart({ product, variant, quantity });
        };
      }
    }
  }, [casaException]);

  useEffect(() => {
    if (customerServiceBoxSystem === CustomerServiceSystem.WHATSAPP) {
      setIsNonCasaChatBoxActive(true);
    }

    if (customerServiceBoxSystem === CustomerServiceSystem.GLADLY) {
      window?.Gladly?.navigate && window?.Gladly?.navigate();
    }
  }, [customerServiceBoxSystem]);

  useEffect(() => {
    if (isNonCasaChatBoxActive) {
      let attempts = 0;
      const maxAttempts = 10;

      const intervalId = window.setInterval(() => {
        if (attempts > maxAttempts) {
          window.clearInterval(intervalId);
          logger.warn('Gladly SDK initialization timeout');
          return;
        }

        attempts++;

        try {
          if (window.Gladly && typeof window.Gladly?.on === 'function' && typeof window.Gladly?.show === 'function') {
            window.Gladly.on('sidekick:opened', () => {
              setIsNoneCasaChatIconHide(true);
            });
            window.Gladly.on('sidekick:closed', () => {
              setIsNoneCasaChatIconHide(false);
            });
            window.clearInterval(intervalId);
          }
        } catch (error: any) {
          logger.warn('Gladly SDK not fully initialized yet:', error);
        }
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [isNonCasaChatBoxActive]);

  const handleCasaOnRuntimeError = useCallback(() => {
    let attempts = 0;
    const maxAttempts = 10;
    let isDone = false;
    loadCasaTimeoutRef.current = window.setInterval(() => {
      if (attempts > maxAttempts || isDone) {
        window.clearInterval(loadCasaTimeoutRef.current!);
        logger.warn('Casa runtime error listener initialization timeout');
        return;
      }

      attempts++;

      try {
        if (window.__CASA_INSTANCE__ && typeof window.__CASA_INSTANCE__.on === 'function') {
          window.__CASA_INSTANCE__.on('runtime:error', (error: any) => {
            logger.warn('Casa runtime error occurred, fallback to Gladly system.', error);
            setCasaException(true);
          });
          isDone = true;
        }
      } catch (error: any) {
        logger.warn('Casa runtime error listener not fully initialized yet:', error);
      }
    }, 1000);
  }, [setCasaException]);

  useEffect(() => {
    // cleanup effect for Casa runtime error listener
    return () => {
      if (loadCasaTimeoutRef.current) {
        window.clearInterval(loadCasaTimeoutRef.current);
      }
      if (window.__CASA_INSTANCE__ && typeof window.__CASA_INSTANCE__.off === 'function') {
        window.__CASA_INSTANCE__.off('runtime:error');
      }
    };
  }, []);

  return (
    <>
      {customerServiceBoxSystem === CustomerServiceSystem.GLADLY && (
        <GladlyLoadScript
          handleOnReady={() => {
            setIsNonCasaChatBoxActive(true);
          }}
        />
      )}
      {isNonCasaChatBoxActive && (
        <Box
          sx={[
            {
              position: 'fixed',
              backgroundColor: (theme) => theme.palette.brand.terracotta[500],
              zIndex: 999,
              right: 0,
              width: '56px',
              height: '70px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, .5)',
              bottom: mobile ? '110px' : '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 12px',
              opacity: isNonCasaChatIconHide ? 0 : 1,
              '.gladly-unread': {
                '#with-unread-message': {
                  display: 'block',
                },
              },
            },
          ]}
        >
          <Button
            sx={{
              width: '100%',
              backgroundColor: 'transparent',
              borderRightWidth: 0,
              borderRadius: 0,
              position: 'relative',
              border: 'none',
              color: (theme: any) => theme.palette.brand.terracotta[500],
              padding: '8px 12px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            id="custom-gladly-chat-button"
            aria-label="Chat"
            onClick={() => {
              if (EcEnv.NEXT_PUBLIC_COUNTRY === 'SG') {
                window.location.href = 'https://wa.me/6582410030';
              } else {
                window?.Gladly?.show?.();
                if (currentUser) {
                  const name = `${currentUser.firstname || ''} ${currentUser.lastname || ''}`;
                  const email = currentUser.email;
                  const _appData =
                    localStorage.getItem(`castlery.com-${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}`) || '';
                  const appData = JSON.parse(_appData);
                  if (appData?.values?.user?.email === currentUser.email && appData?.values?.user?.name === name) {
                    return;
                  }
                  window.Gladly?.setUser &&
                    window.Gladly?.setUser({
                      name,
                      email,
                    });
                }
              }
            }}
          >
            <Box
              sx={[
                {
                  position: 'absolute',
                  background: (theme) => theme.palette.brand.terracotta[500],
                  border: '2px solid #fff',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, .5)',
                  top: '4px',
                  left: '14px',
                  height: '10px',
                  width: '10px',
                  display: 'none',
                },
                mobile && {
                  top: '1px',
                  left: '5px',
                },
              ]}
              id="with-unread-message"
            />
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingBottom: '4px',
                }}
              >
                <Communication
                  sx={[
                    {
                      width: '24px !important',
                      height: '22.77px !important',
                    },
                  ]}
                />
              </Stack>
              <Typography
                level="body2"
                sx={{
                  textTransform: 'capitalize !important',
                  color: (theme) => theme.palette.brand.warmLinen[500],
                }}
              >
                Chat
              </Typography>
            </Stack>
          </Button>
        </Box>
      )}
      {customerServiceBoxSystem === CustomerServiceSystem.CASA && (
        <CasaLoadScript
          handleOnError={() => {
            setCasaException(true);
            Sentry.captureException(new Error('CasaLoadScript failed to load'), {
              tags: {
                issue: 'customer-service-casa-load-error',
              },
            });
          }}
          handleOnLoad={handleCasaOnRuntimeError}
        />
      )}
    </>
  );
};

export { CustomerServiceBoxImpl };
